import React, { useState, useEffect } from 'react';
import API from "../../utils/API";
import { LineChart, Line, BarChart, Bar, PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';

const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884d8', '#82ca9d'];

/**
 * Rótulos das etapas do kanban.
 *
 * As chaves são os identificadores crus que vêm do backend (parte deles em
 * caixa alta, herdada de services.sub_status). Antes o rótulo era gerado por
 * title-case automático, que produzia coisas como "Retirada E Pagamento" —
 * capitalizando a conjunção. Mapear explicitamente resolve isso e permite
 * renomear a etapa sem mexer na chave usada no agrupamento.
 */
const STAGE_LABELS = {
  'Aguardando Checklist': 'Aguardando checklist',
  'Aguardando Aprovação do Chefe': 'Aguardando aprovação do chefe',
  'Checklist Rejeitado': 'Checklist rejeitado',
  'Aguardando Compras': 'Aguardando compras',
  'Aprovação do Orçamento': 'Aguardando aprovação do orçamento',
  'Aceito Pelo Cliente - OS Emitida': 'Aprovado pelo cliente',
  'AGUARDANDO PEÇA': 'Aguardando peça',
  'Atribuir Mecânico': 'Atribuir mecânico',
  'EM REPARO': 'Em reparo',
  'DISPONÍVEL PARA TESTE': 'Disponível para teste',
  'DISPONÍVEL PARA RETIRADA E PAGAMENTO': 'Disponível para retirada e pagamento',
};

const StatisticsDashboard = () => {
  const [loading, setLoading] = useState(true);
  const [timeRange, setTimeRange] = useState('30d');
  const [customStart, setCustomStart] = useState('');
  const [customEnd, setCustomEnd] = useState('');
  const [appliedCustomStart, setAppliedCustomStart] = useState('');
  const [appliedCustomEnd, setAppliedCustomEnd] = useState('');
  const [activeTab, setActiveTab] = useState('gestao');
  const [workMetrics, setWorkMetrics] = useState([]);
  const [stats, setStats] = useState({
    totalServices: 0,
    totalClients: 0,
    totalVehicles: 0,
    servicesInProgress: 0,
    servicesCompleted: 0,
    averageServicePrice: 0,
    servicesByMonth: [],
    servicesByType: [],
    vehiclesByModel: [],
    topServices: [],
    revenueByMonth: []
  });
  const [executorFilter, setExecutorFilter] = useState("");

  const formatBrazilianDate = (dateStr) => {
    if (!dateStr) return "-";
    // Accept ISO (YYYY-MM-DD), full ISO datetime, or RFC 1123 ("Mon, 20 Apr 2026 00:00:00 GMT")
    const iso = /^\d{4}-\d{2}-\d{2}$/.test(dateStr) ? `${dateStr}T00:00:00` : dateStr;
    const date = new Date(iso);
    if (isNaN(date.getTime())) return "-";
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    return `${day}/${month}/${year}`;
  };

  useEffect(() => {
    const user = JSON.parse(localStorage.getItem("yc_usr_dt_ls"));
    if (user && ["MECANICO", "COMPRADOR", "RECEPCAO"].includes(user.role)) {
      window.location.href = '/services';
    }
  }, []);

  useEffect(() => {
    setLoading(true);

    if (!localStorage.getItem('yc_usr_dt_ls')) {
      window.location.href = '/login';
    }

    // If "custom" is selected but dates haven't been applied yet, don't fetch
    if (timeRange === 'custom' && (!appliedCustomStart || !appliedCustomEnd)) {
      setLoading(false);
      return;
    }

    const startDate = timeRange === 'custom' ? appliedCustomStart : undefined;
    const endDate = timeRange === 'custom' ? appliedCustomEnd : undefined;

    API.getStatistics(API.cancelToken(), timeRange, undefined, startDate, endDate)
      .then(response => {
        setStats(response.data);
        setLoading(false);
      });

    API.getWorkerMetrics(API.cancelToken(), timeRange, undefined, startDate, endDate)
      .then(response => {
        setWorkMetrics(response.data);
      });

  }, [timeRange, appliedCustomStart, appliedCustomEnd]);

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value);
  };

  /**
   * Variação contra o período anterior de mesma duração.
   * null = não havia base anterior para comparar, então não inventamos um %.
   */
  const formatChange = (pct) => {
    if (pct === null || pct === undefined) return 'sem base anterior';
    const sinal = pct > 0 ? '▲' : pct < 0 ? '▼' : '';
    return `${sinal} ${Math.abs(pct).toLocaleString('pt-BR')}% vs. período anterior`;
  };

  const exportToExcel = () => {
    const source = workMetrics && workMetrics.length > 0 ? workMetrics : [];
    // Re-map columns to match the UI: Mecânico | Serviço | Qtd | Data | Valor Serviço | Valor Peças | Valor Total
    // (Valor Serviço = mão-de-obra/HHT, Valor Peças = peças)
    const data = source.map((r) => ({
      "Mecânico": r.executor || "",
      "Serviço Executado": r.servico || "",
      "Quantidade": r.quantidade || 0,
      "Data": r.data || "",
      "Valor Serviço": Number(r.valor_hht) || 0,
      "Valor Peças": Number(r.valor_em_pecas) || 0,
      "Valor Total": Number(r.valor) || 0,
    }));
    const worksheet = XLSX.utils.json_to_sheet(data);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Serviços por Executor");
    const excelBuffer = XLSX.write(workbook, { bookType: "xlsx", type: "array" });
    const dataBlob = new Blob([excelBuffer], { type: "application/octet-stream" });
    saveAs(dataBlob, "servicos_por_executor.xlsx");
  };

  const filteredWorkMetrics = workMetrics && executorFilter.trim()
    ? workMetrics.filter(item => item.executor.toLowerCase().includes(executorFilter.toLowerCase()))
    : workMetrics;

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500 mx-auto"></div>
          <p className="mt-4 text-gray-700">Carregando estatísticas...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 pb-10">
      <div className="container mx-auto px-4 py-6">
        <div className="bg-white rounded-lg shadow-md p-6 mb-6">
          {/* Header */}
          <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-6 gap-4">
            <h1 className="text-2xl md:text-3xl font-bold text-gray-800">
              Dashboard de Estatísticas
            </h1>
            <div className="flex flex-col md:flex-row items-start md:items-center gap-3 w-full md:w-auto">
              <select
                value={timeRange}
                onChange={(e) => {
                  setTimeRange(e.target.value);
                  if (e.target.value !== 'custom') {
                    setAppliedCustomStart('');
                    setAppliedCustomEnd('');
                  }
                }}
                className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 p-2.5 w-full md:w-auto"
              >
                <option value="30d">Últimos 30 dias</option>
                <option value="60d">Últimos 60 dias</option>
                <option value="90d">Últimos 90 dias</option>
                <option value="custom">Selecionar Datas</option>
              </select>

              {timeRange === 'custom' && (
                <div className="flex flex-col sm:flex-row items-start sm:items-center gap-2 w-full md:w-auto">
                  <input
                    type="date"
                    value={customStart}
                    max={new Date().toISOString().slice(0, 10)}
                    onChange={(e) => setCustomStart(e.target.value)}
                    className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 p-2.5"
                  />
                  <span className="text-gray-500 text-sm">até</span>
                  <input
                    type="date"
                    value={customEnd}
                    max={new Date().toISOString().slice(0, 10)}
                    min={customStart || undefined}
                    onChange={(e) => setCustomEnd(e.target.value)}
                    className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 p-2.5"
                  />
                  <button
                    type="button"
                    onClick={() => {
                      if (customStart && customEnd) {
                        setAppliedCustomStart(customStart);
                        setAppliedCustomEnd(customEnd);
                      }
                    }}
                    disabled={!customStart || !customEnd}
                    className={`px-4 py-2 rounded-lg text-white text-sm font-medium ${
                      !customStart || !customEnd
                        ? 'bg-blue-300 cursor-not-allowed'
                        : 'bg-blue-600 hover:bg-blue-700'
                    }`}
                  >
                    Aplicar
                  </button>
                </div>
              )}
            </div>
          </div>

          {/* Tabs */}
          <div className="flex border-b border-gray-200 mb-6">
            <button
              onClick={() => setActiveTab('gestao')}
              className={`px-6 py-3 text-sm font-medium border-b-2 transition-colors ${
                activeTab === 'gestao'
                  ? 'border-blue-600 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              Gestão
            </button>
            <button
              onClick={() => setActiveTab('operacional')}
              className={`px-6 py-3 text-sm font-medium border-b-2 transition-colors ${
                activeTab === 'operacional'
                  ? 'border-blue-600 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              Operacional
            </button>
          </div>

          {/* ===================== GESTÃO TAB ===================== */}
          {activeTab === 'gestao' && (
            <>
              {/* Stats Cards */}
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
                <StatsCard title="O.S Abertas" value={stats.servicesOpened ?? 0} icon="📋" color="bg-blue-100" textColor="text-blue-800" />
                <StatsCard title="O.S Encerradas" value={stats.servicesClosed ?? 0} icon="✅" color="bg-green-100" textColor="text-green-800" />
                <StatsCard
                  title="Clientes"
                  value={stats.totalClients}
                  icon="👥"
                  color="bg-teal-100"
                  textColor="text-teal-800"
                  footnote={formatChange(stats.clientsChangePct)}
                />
                <StatsCard
                  title="Veículos"
                  value={stats.totalVehicles}
                  icon="🚗"
                  color="bg-purple-100"
                  textColor="text-purple-800"
                  footnote={formatChange(stats.vehiclesChangePct)}
                />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
                <StatsCard
                  title="Faturamento Mão de Obra"
                  value={formatCurrency(stats.revenueLabour || 0)}
                  icon="🔧"
                  color="bg-yellow-100"
                  textColor="text-yellow-800"
                  footnote={`${(stats.revenueLabourPct || 0).toLocaleString('pt-BR')}% do total · média ${formatCurrency(stats.revenueLabourAvg || 0)}/O.S`}
                />
                <StatsCard
                  title="Faturamento Peças"
                  value={formatCurrency(stats.revenueParts || 0)}
                  icon="⚙️"
                  color="bg-orange-100"
                  textColor="text-orange-800"
                  footnote={`${(stats.revenuePartsPct || 0).toLocaleString('pt-BR')}% do total · média ${formatCurrency(stats.revenuePartsAvg || 0)}/O.S`}
                />
                <StatsCard
                  title="Faturamento Total"
                  value={formatCurrency(stats.revenueTotal || 0)}
                  icon="💰"
                  color="bg-indigo-100"
                  textColor="text-indigo-800"
                  footnote={`${stats.revenueOsCount || 0} O.S · média ${formatCurrency(stats.revenueTotalAvg || 0)}/O.S`}
                />
                <StatsCard
                  title="Tempo Médio de Serviço"
                  value={`${(stats.averageServiceTime || 0).toLocaleString('pt-BR')} dias`}
                  icon="⏱️"
                  color="bg-pink-100"
                  textColor="text-pink-800"
                  footnote="Da abertura da O.S até a entrega"
                />
              </div>

              {/* Revenue & Services by Month */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
                <ChartCard title="Serviços por Mês">
                  <ResponsiveContainer width="100%" height={300}>
                    <LineChart data={stats.servicesByMonth} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="name" />
                      <YAxis />
                      <Tooltip formatter={(value) => [`${value} serviços`, 'Quantidade']} />
                      <Legend />
                      <Line type="monotone" dataKey="count" stroke="#8884d8" activeDot={{ r: 8 }} name="Serviços" />
                    </LineChart>
                  </ResponsiveContainer>
                </ChartCard>

                <ChartCard title="Receita Mensal">
                  <ResponsiveContainer width="100%" height={300}>
                    <BarChart data={stats.revenueByMonth} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="name" />
                      <YAxis />
                      <Tooltip formatter={(value, name) => [formatCurrency(value), name]} />
                      <Legend />
                      {/* Duas séries: "Aprovado" acompanha o movimento corrente da
                          oficina (mês da aprovação do cliente) e "Entregue" é o
                          número contábil (mês da entrega). O vão entre as duas
                          mostra as OSs executadas que não foram fechadas. */}
                      <Bar dataKey="approved" fill="#93C5FD" name="Aprovado pelo cliente" />
                      <Bar dataKey="delivered" fill="#82ca9d" name="Entregue" />
                    </BarChart>
                  </ResponsiveContainer>
                </ChartCard>
              </div>

              {/* Vehicle Models & Brands */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
                <ChartCard title="Veículos por Modelo (Top 10)">
                  <ResponsiveContainer width="100%" height={300}>
                    <BarChart data={stats.vehiclesByFullModel || []} margin={{ top: 5, right: 30, left: 20, bottom: 60 }}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="name" angle={-35} textAnchor="end" interval={0} tick={{ fontSize: 11 }} />
                      <YAxis allowDecimals={false} />
                      <Tooltip formatter={(value) => [`${value} veículos`, 'Quantidade']} />
                      <Bar dataKey="count" fill="#8884d8" name="Veículos" radius={[4, 4, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </ChartCard>

                <ChartCard title="Veículos por Marca">
                  <ResponsiveContainer width="100%" height={300}>
                    <BarChart data={stats.vehiclesByModel} layout="vertical" margin={{ top: 5, right: 30, left: 50, bottom: 5 }}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis type="number" allowDecimals={false} />
                      <YAxis dataKey="name" type="category" />
                      <Tooltip formatter={(value) => [`${value} veículos`, 'Quantidade']} />
                      <Bar dataKey="count" fill="#82ca9d" name="Veículos" radius={[0, 4, 4, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </ChartCard>
              </div>

              {/* Top Services */}
              <ChartCard title="Serviços Mais Solicitados">
                <div className="overflow-x-auto">
                  <table className="min-w-full bg-white">
                    <thead>
                      <tr className="bg-gray-100 text-left">
                        <th className="py-3 px-4 font-semibold text-sm text-gray-700 border-b">Serviço</th>
                        <th className="py-3 px-4 font-semibold text-sm text-gray-700 border-b text-center">Quantidade</th>
                        <th className="py-3 px-4 font-semibold text-sm text-gray-700 border-b text-right">Valor Total</th>
                      </tr>
                    </thead>
                    <tbody>
                      {stats.topServices.map((service, index) => (
                        <tr key={index} className={index % 2 === 0 ? "bg-white" : "bg-gray-50"}>
                          <td className="py-3 px-4 border-b border-gray-100">{service.name}</td>
                          <td className="py-3 px-4 border-b border-gray-100 text-center">{service.count}</td>
                          <td className="py-3 px-4 border-b border-gray-100 text-right font-medium">{formatCurrency(service.value)}</td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot>
                      <tr className="bg-gray-50">
                        <td className="py-3 px-4 font-semibold">Total</td>
                        <td className="py-3 px-4 text-center font-semibold">
                          {stats.topServices.reduce((sum, s) => sum + s.count, 0)}
                        </td>
                        <td className="py-3 px-4 text-right font-semibold">
                          {formatCurrency(stats.topServices.reduce((sum, s) => sum + (parseFloat(s.value) || 0), 0))}
                        </td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              </ChartCard>

              {/* Worker Metrics (movido do Operacional para Gestão) */}
              <div className="mb-6">
                <ChartCard title="Serviços Executados por Mecânico">
                  <div className="flex flex-col md:flex-row w-full mb-4 gap-4">
                    <input
                      type="text"
                      placeholder="Filtrar por mecânico..."
                      value={executorFilter}
                      onChange={(e) => setExecutorFilter(e.target.value)}
                      className="border border-gray-300 p-2 rounded w-full md:w-2/3"
                    />
                    <button
                      onClick={exportToExcel}
                      className="bg-green-600 text-white px-4 py-2 rounded hover:bg-green-700 w-full md:w-auto"
                    >
                      Exportar para Excel
                    </button>
                  </div>

                  <div className="overflow-x-auto">
                    <table className="min-w-full bg-white text-sm text-left">
                      <thead className="bg-gray-100">
                        <tr>
                          <th className="py-2 px-4">Mecânico</th>
                          <th className="py-2 px-4">Serviço Executado</th>
                          <th className="py-2 px-4">Quantidade</th>
                          <th className="py-2 px-4">Data</th>
                          <th className="py-2 px-4">Valor Serviço</th>
                          <th className="py-2 px-4">Valor Peças</th>
                          <th className="py-2 px-4">Valor Total</th>
                        </tr>
                      </thead>
                      <tbody>
                        {filteredWorkMetrics && filteredWorkMetrics.length > 0 ? (
                          filteredWorkMetrics.map((item, index) => (
                            <tr key={index} className={index % 2 === 0 ? "bg-white" : "bg-gray-50"}>
                              <td className="py-2 px-4">{item.executor}</td>
                              <td className="py-2 px-4">{item.servico}</td>
                              <td className="py-2 px-4">{item.quantidade}</td>
                              <td className="py-2 px-4">{formatBrazilianDate(item.data)}</td>
                              <td className="py-2 px-4">{formatCurrency(item.valor_hht)}</td>
                              <td className="py-2 px-4">{formatCurrency(item.valor_em_pecas)}</td>
                              <td className="py-2 px-4">{formatCurrency(item.valor)}</td>
                            </tr>
                          ))
                        ) : (
                          <tr>
                            <td colSpan="7" className="py-4 text-center text-gray-500">
                              Nenhum resultado encontrado.
                            </td>
                          </tr>
                        )}
                      </tbody>
                      {filteredWorkMetrics && filteredWorkMetrics.length > 0 && (
                        <tfoot>
                          <tr className="bg-gray-100 font-semibold">
                            <td className="py-2 px-4" colSpan="2">Total</td>
                            <td className="py-2 px-4">
                              {filteredWorkMetrics.reduce((sum, i) => sum + (parseInt(i.quantidade) || 0), 0)}
                            </td>
                            <td className="py-2 px-4">—</td>
                            <td className="py-2 px-4">
                              {formatCurrency(filteredWorkMetrics.reduce((sum, i) => sum + (parseFloat(i.valor_hht) || 0), 0))}
                            </td>
                            <td className="py-2 px-4">
                              {formatCurrency(filteredWorkMetrics.reduce((sum, i) => sum + (parseFloat(i.valor_em_pecas) || 0), 0))}
                            </td>
                            <td className="py-2 px-4">
                              {formatCurrency(filteredWorkMetrics.reduce((sum, i) => sum + (parseFloat(i.valor) || 0), 0))}
                            </td>
                          </tr>
                        </tfoot>
                      )}
                    </table>
                  </div>
                </ChartCard>
              </div>
            </>
          )}

          {/* ===================== OPERACIONAL TAB ===================== */}
          {activeTab === 'operacional' && (
            <>
              {/* Pipeline — Vehicles per Stage (kanban) */}
              {stats.servicesByStage && stats.servicesByStage.length > 0 && (
                <div className="mb-6">
                  <ChartCard title="Veículos por Etapa do Processo">
                    <div className="pb-2">
                      <div className="flex flex-col gap-4">
                        {stats.servicesByStage.map((col, index) => {
                          const stageColors = [
                            { head: 'bg-blue-100 text-blue-800 border-blue-300', dot: 'bg-blue-500' },
                            { head: 'bg-yellow-100 text-yellow-800 border-yellow-300', dot: 'bg-yellow-500' },
                            { head: 'bg-red-100 text-red-800 border-red-300', dot: 'bg-red-500' },
                            { head: 'bg-orange-100 text-orange-800 border-orange-300', dot: 'bg-orange-500' },
                            { head: 'bg-purple-100 text-purple-800 border-purple-300', dot: 'bg-purple-500' },
                            { head: 'bg-teal-100 text-teal-800 border-teal-300', dot: 'bg-teal-500' },
                            { head: 'bg-pink-100 text-pink-800 border-pink-300', dot: 'bg-pink-500' },
                            { head: 'bg-indigo-100 text-indigo-800 border-indigo-300', dot: 'bg-indigo-500' },
                            { head: 'bg-cyan-100 text-cyan-800 border-cyan-300', dot: 'bg-cyan-500' },
                            { head: 'bg-emerald-100 text-emerald-800 border-emerald-300', dot: 'bg-emerald-500' },
                          ];
                          const c = stageColors[index % stageColors.length];
                          const stageLabel = STAGE_LABELS[col.stage] || col.stage;
                          const items = Array.isArray(col.items) ? col.items : [];
                          return (
                            <div key={col.stage} className="w-full bg-gray-50 rounded-lg border border-gray-200">
                              <div className={`flex items-center justify-between px-3 py-2 rounded-t-lg border-b-2 ${c.head}`}>
                                <span className="text-sm font-semibold leading-tight">{stageLabel}</span>
                                <span className="ml-2 inline-flex items-center justify-center min-w-[22px] h-[22px] px-1.5 rounded-full bg-white/70 text-xs font-bold">
                                  {col.count}
                                </span>
                              </div>
                              <div className="p-2 flex flex-wrap gap-2">
                                {items.length === 0 ? (
                                  <p className="text-xs text-gray-400 py-2 px-1">Nenhum veículo</p>
                                ) : (
                                  items.map((os) => (
                                    <a
                                      key={os.service_id}
                                      href={`/order?id=${os.service_id}`}
                                      className="block w-48 max-md:w-full bg-white rounded-md border border-gray-200 p-2.5 hover:border-[#389E0D] hover:shadow-sm transition-all"
                                    >
                                      <div className="flex items-center gap-1.5">
                                        <span className={`inline-block w-1.5 h-1.5 rounded-full ${c.dot}`} />
                                        <span className="text-xs font-bold text-gray-800">O.S #{os.service_id}</span>
                                      </div>
                                      <p className="text-xs text-gray-600 mt-1">
                                        Placa: <span className="font-mono font-medium">{os.license_plate || '-'}</span>
                                      </p>
                                      <p className="text-xs text-gray-600">
                                        Veículo: {os.model || '-'}
                                      </p>
                                      {/* Pago / Retirado: a OS só encerra com os dois */}
                                      {(os.paid || os.picked_up) && (
                                        <div className="flex flex-wrap gap-1 mt-1.5">
                                          {os.paid && (
                                            <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-semibold bg-green-100 text-green-800">
                                              Pago
                                            </span>
                                          )}
                                          {os.picked_up && (
                                            <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-semibold bg-blue-100 text-blue-800">
                                              Retirado
                                            </span>
                                          )}
                                        </div>
                                      )}
                                    </a>
                                  ))
                                )}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  </ChartCard>
                </div>
              )}

              {/* Promised vs Actual Delivery */}
              {stats.deliverySummary && (
                <div className="mb-6">
                  <ChartCard title="Prazo Prometido vs Entrega Real">
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                      <div className="p-4 rounded-lg border-2 text-center bg-green-100 text-green-800 border-green-300">
                        <p className="text-3xl font-bold">{stats.deliverySummary.onTime}</p>
                        <p className="text-sm font-medium mt-1">No Prazo ou Adiantado</p>
                      </div>
                      <div className="p-4 rounded-lg border-2 text-center bg-red-100 text-red-800 border-red-300">
                        <p className="text-3xl font-bold">{stats.deliverySummary.late}</p>
                        <p className="text-sm font-medium mt-1">Atrasados</p>
                      </div>
                      <div className={`p-4 rounded-lg border-2 text-center ${
                        stats.deliverySummary.avgDelayDays > 0
                          ? 'bg-red-50 text-red-700 border-red-200'
                          : 'bg-green-50 text-green-700 border-green-200'
                      }`}>
                        <p className="text-3xl font-bold">{stats.deliverySummary.avgDelayDays}d</p>
                        <p className="text-sm font-medium mt-1">
                          {stats.deliverySummary.avgDelayDays > 0 ? 'Atraso Médio' : 'Adiantamento Médio'}
                        </p>
                      </div>
                    </div>

                    {stats.deliveryComparison && stats.deliveryComparison.length > 0 && (
                      <div className="overflow-x-auto">
                        <table className="min-w-full bg-white text-sm text-left">
                          <thead className="bg-gray-100">
                            <tr>
                              <th className="py-2 px-4">OS</th>
                              <th className="py-2 px-4">Cliente</th>
                              <th className="py-2 px-4">Placa</th>
                              <th className="py-2 px-4">Prometido</th>
                              <th className="py-2 px-4">Entregue</th>
                              <th className="py-2 px-4">Diferença</th>
                            </tr>
                          </thead>
                          <tbody>
                            {stats.deliveryComparison.map((row, index) => (
                              <tr key={row.service_id} className={index % 2 === 0 ? "bg-white" : "bg-gray-50"}>
                                <td className="py-2 px-4 font-medium">#{row.service_id}</td>
                                <td className="py-2 px-4">{row.client_name || '-'}</td>
                                <td className="py-2 px-4">{row.license_plate || '-'}</td>
                                <td className="py-2 px-4">{formatBrazilianDate(row.promised)}</td>
                                <td className="py-2 px-4">{formatBrazilianDate(row.delivered)}</td>
                                <td className="py-2 px-4">
                                  {row.diff_days !== null && (
                                    <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
                                      row.diff_days > 0
                                        ? 'bg-red-100 text-red-800'
                                        : row.diff_days === 0
                                        ? 'bg-gray-100 text-gray-800'
                                        : 'bg-green-100 text-green-800'
                                    }`}>
                                      {row.diff_days > 0 ? `+${row.diff_days}d atraso` : row.diff_days === 0 ? 'No prazo' : `${Math.abs(row.diff_days)}d adiantado`}
                                    </span>
                                  )}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                          <tfoot>
                            <tr className="bg-gray-100 font-semibold">
                              <td className="py-2 px-4" colSpan="5">Total de OS: {stats.deliveryComparison.length}</td>
                              <td className="py-2 px-4">
                                {(() => {
                                  const valid = stats.deliveryComparison.filter(r => r.diff_days !== null && r.diff_days !== undefined);
                                  if (!valid.length) return '—';
                                  const avg = valid.reduce((s, r) => s + Number(r.diff_days), 0) / valid.length;
                                  const rounded = Math.round(avg * 10) / 10;
                                  if (rounded > 0) return `Média: +${rounded}d atraso`;
                                  if (rounded < 0) return `Média: ${Math.abs(rounded)}d adiantado`;
                                  return 'Média: no prazo';
                                })()}
                              </td>
                            </tr>
                          </tfoot>
                        </table>
                      </div>
                    )}
                  </ChartCard>
                </div>
              )}

              {/* Average Time by Service Type */}
              {stats.avgTimeByServiceType && stats.avgTimeByServiceType.length > 0 && (
                <div className="mb-6">
                  <ChartCard title="Tempo Médio de Execução por Serviço">
                    <ResponsiveContainer width="100%" height={Math.max(300, stats.avgTimeByServiceType.length * 40)}>
                      <BarChart
                        data={stats.avgTimeByServiceType}
                        layout="vertical"
                        margin={{ top: 5, right: 30, left: 150, bottom: 5 }}
                      >
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis type="number" unit="h" />
                        <YAxis dataKey="name" type="category" width={140} tick={{ fontSize: 12 }} />
                        <Tooltip
                          formatter={(value, name) => {
                            if (name === 'Tempo Médio') return [`${value}h`, name];
                            return [value, name];
                          }}
                        />
                        <Legend />
                        <Bar dataKey="avg_hours" fill="#F97316" name="Tempo Médio" radius={[0, 4, 4, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  </ChartCard>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

const StatsCard = ({ title, value, icon, color, textColor, footnote }) => (
  <div className={`p-4 rounded-lg shadow-sm overflow-hidden ${color}`}>
    <div className="flex items-center gap-3 min-w-0">
      <div className={`text-2xl xl:text-3xl shrink-0 ${textColor}`}>{icon}</div>
      <div className="min-w-0 flex-1">
        <p className="text-xs xl:text-sm text-gray-600 truncate" title={title}>{title}</p>
        <p
          className={`text-lg xl:text-xl font-bold leading-tight break-words ${textColor}`}
          title={typeof value === "string" ? value : undefined}
        >
          {value}
        </p>
        {footnote && (
          <p className="text-[11px] text-gray-600 leading-tight mt-0.5">{footnote}</p>
        )}
      </div>
    </div>
  </div>
);

const ChartCard = ({ title, children }) => (
  <div className="bg-white rounded-lg shadow-sm p-4">
    <h2 className="text-lg font-semibold mb-4 text-gray-800">{title}</h2>
    {children}
  </div>
);

export default StatisticsDashboard;
